import secureApiService from './secureApiService'
import {
  ProgrammeModel,
  ProgrammeJSON,
  ProgrammePaymentAccountJSON,
  ProgrammesResponseJSON,
  ProgrammeSegmentModel,
  ProgrammeAdmissionRequirementsModel,
} from '@/models/programme.model'
import { PaginatedResponse, fetchAllPages } from '@/models/templates/pagination.model'

export interface ProgrammeSegmentMutationPayload {
  name?: string | null
  length?: number | null
  unit?: string | null
  order?: number | null
  description?: string | null
}

export interface ProgrammeAdmissionRequirementsMutationPayload {
  requires_jamb?: boolean | null
  requires_o_level?: boolean | null
  required_documents?: string[] | null
}

export interface ProgrammeMutationPayload {
  title: string
  description: string
  duration: number
  duration_unit: string
  type: string
  is_active: boolean
  departmentId?: string | null
  segments: ProgrammeSegmentMutationPayload[]
  admission_requirements?: ProgrammeAdmissionRequirementsMutationPayload
}

interface ProgrammePageParams {
  page?: number
  perPage?: number
}

interface RequestOptions {
  requireAuth?: boolean
  skipCSRF?: boolean
}

interface ProgrammeSingleResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeJSON | null
}

class ProgrammesService {
  private basePath = '/programmes'

  async getProgrammesPage(
    params: ProgrammePageParams = {},
    options: RequestOptions = {},
  ): Promise<PaginatedResponse<ProgrammeModel>> {
    const page = params.page ?? 1
    const perPage = params.perPage ?? 25
    const query = new URLSearchParams({ page: String(page), perPage: String(perPage) })

    const response = await secureApiService.get<ProgrammesResponseJSON>(`${this.basePath}?${query.toString()}`, {
      requireAuth: options.requireAuth ?? true,
      skipCSRF: options.skipCSRF ?? false,
    })

    const payload = response.data

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch programmes')
    }

    const dataArray = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    const data = dataArray
      .filter((programme): programme is ProgrammeJSON => Boolean(programme))
      .map((programme) => ProgrammeModel.fromJSON(programme))

    const meta = {
      total: payload.meta?.total ?? dataArray.length,
      page: payload.meta?.page ?? page,
      perPage: payload.meta?.perPage ?? perPage,
    }

    return { data, meta }
  }

  async getAllProgrammes(options: RequestOptions & { perPage?: number } = {}): Promise<PaginatedResponse<ProgrammeModel>> {
    const perPage = options.perPage ?? 50
    return fetchAllPages((page) => this.getProgrammesPage({ page, perPage }, options), perPage)
  }

  async getProgrammes(options: RequestOptions & { perPage?: number } = {}): Promise<ProgrammeModel[]> {
    const response = await this.getAllProgrammes(options)
    return response.data
  }

  async getProgramme(programmeId: string, options: RequestOptions = {}): Promise<ProgrammeModel> {
    const response = await secureApiService.get<ProgrammeSingleResponseJSON>(`${this.basePath}/${programmeId}`, {
      requireAuth: options.requireAuth ?? true,
      skipCSRF: options.skipCSRF ?? false,
    })

    const payload = response.data

    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch programme details')
    }

    return ProgrammeModel.fromJSON(payload.data)
  }

  async createProgramme(payload: ProgrammeMutationPayload): Promise<ProgrammeModel> {
    const response = await secureApiService.post<{
      success: boolean
      message?: string
      data?: ProgrammeJSON
    }>(this.basePath, {
      title: payload.title,
      description: payload.description,
      duration: payload.duration,
      duration_unit: payload.duration_unit,
      type: payload.type,
      is_active: payload.is_active,
      departmentId: payload.departmentId ?? undefined,
      segments: payload.segments,
      admission_requirements: payload.admission_requirements,
    })

    const body = response.data

    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create programme')
    }

    return ProgrammeModel.fromJSON(body.data)
  }

  async updateProgramme(id: string, payload: ProgrammeMutationPayload): Promise<ProgrammeModel> {
    const response = await secureApiService.put<{
      success: boolean
      message?: string
      data?: ProgrammeJSON
    }>(`${this.basePath}/${id}`, {
      title: payload.title,
      description: payload.description,
      duration: payload.duration,
      duration_unit: payload.duration_unit,
      type: payload.type,
      isActive: payload.is_active,
      departmentId: payload.departmentId ?? undefined,
      segments: payload.segments,
      admission_requirements: payload.admission_requirements,
    })

    const body = response.data

    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update programme')
    }

    return ProgrammeModel.fromJSON(body.data)
  }

  async deleteProgramme(id: string): Promise<void> {
    const response = await secureApiService.delete<{
      success: boolean
      message?: string
    }>(`${this.basePath}/${id}`)

    const body = response.data

    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete programme')
    }
  }
}

export const programmesService = new ProgrammesService()
